<template>
  <div class="robofinder-results">
    <div class="loading" v-if="loading">
      <img src='/images/loading.gif'>
    </div>

    <div class="items" v-else>
      <div class="no-item" v-if="results.length == 0" style="text-align: center; width: 100%; padding: 40px 20px;">
        {{ $t('no_results') }}
      </div>

      <div class="item" v-for="item in results" :key="item.id">
        <search-page-item
          :item="item"
          :see-detail-text="$t('see_detail')"
          :is-signed-in="isSignedIn"
          :is-mobile="isMobile"
          :open-in-new-tab="true"
        />
      </div>
    </div>

    <!-- Pagination -->
    <div class="results-pagination" v-if="totalPages > 1">
      <b-pagination
        :total="totalCount"
        :current.sync="currentPage"
        :per-page="perPage"
        @change="onPageChange"
        icon-pack="custom"
      >
        <template #previous>
          <img src="/images/left_switch.png" class="pagination-nav-icon" alt="Previous">
        </template>
        <template #next>
          <img src="/images/right_switch.png" class="pagination-nav-icon" alt="Next">
        </template>
      </b-pagination>
    </div>
  </div>
</template>

<script>
const axios = require('axios');
import SearchPageItem from '../search_page_item.vue'

export default {
  components: {
    SearchPageItem,
  },

  props: {
    selections: {
      type: Object,
      required: true
    },
    apiResultsUrl: {
      type: String,
      required: true
    },
    isSignedIn: {
      type: String,
      default: 'false'
    },
    isMobile: {
      type: String,
      default: 'false'
    },
    translations: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      loading: false,
      results: [],
      currentPage: 1,
      totalPages: 0,
      totalCount: 0,
      perPage: 20
    }
  },

  methods: {
    async fetchResults() {
      this.loading = true;

      try {
        const params = {
          ...this.selections,
          page: this.currentPage,
          per: this.perPage
        };

        // Remove null/undefined values
        Object.keys(params).forEach(key => {
          if (params[key] == null) {
            delete params[key];
          }
        });

        const response = await axios.get(this.apiResultsUrl, {
          params: params,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });

        this.results = response.data.results;
        this.currentPage = response.data.pagination.current_page;
        this.totalPages = response.data.pagination.total_pages;
        this.totalCount = response.data.pagination.total_count;
        this.perPage = response.data.pagination.per_page;

        // Emit result count to parent
        this.$emit('result-count-changed', this.totalCount);

        this.loading = false;
      } catch (error) {
        console.error('Error fetching results:', error);
        this.loading = false;
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchResults();
      // Scroll to top of results
      this.$el.scrollIntoView({ behavior: 'smooth' });
    },

    $t(key) {
      // Use translations passed from Rails via props
      return this.translations?.results?.[key] || key;
    }
  },

  watch: {
    selections: {
      handler() {
        // Reset to page 1 when selections change
        this.currentPage = 1;
        this.fetchResults();
      },
      deep: true
    }
  },

  mounted() {
    this.fetchResults();
  }
}
</script>

<style lang="scss" scoped>
.robofinder-results {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
  }

  .results-header {
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid #e0e0e0;

    h3 {
      font-size: 24px;
      font-weight: bold;
      margin: 0 0 10px 0;
    }

    .results-count {
      color: #666;
      font-size: 14px;
    }
  }

  .loading {
    text-align: center;
    padding: 50px;
  }

  .items {
    @media (max-width: 768px) {
      width: 100%;

      .item {
        width: 100%;
      }
    }

    .no-item {
      text-align: center;
      padding: 40px 20px;
      color: #666;
    }
  }

  .results-pagination {
    display: flex;
    justify-content: center;
    margin-top: 30px;

    .pagination-nav-icon {
      width: 32px;
      height: 32px;
      object-fit: contain;
    }

    ::v-deep .pagination-previous:hover .pagination-nav-icon {
      content: url('/images/left_switch_hover.png');
    }

    ::v-deep .pagination-next:hover .pagination-nav-icon {
      content: url('/images/right_switch_hover.png');
    }

    ::v-deep .pagination-previous,
    ::v-deep .pagination-next {
      border: none;
      background: transparent;
    }
  }
}

// Items and item styles are inherited from global user/common.scss
// This ensures consistent design with search page
</style>
